import { useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtQty, type StockSummaryRow } from "@/types/inventory";
import { Swatch } from "./StockStatusBadge";

interface Props {
  warehouseId: string;
  search: string;
  reloadKey: number;
  onOpenRolls: (row: StockSummaryRow) => void;
}

/** Stock on hand by product and shade */
export function StockTab({ warehouseId, search, reloadKey, onOpenRolls }: Props) {
  const { error } = useToast();
  const [rows, setRows] = useState<StockSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      const qs = new URLSearchParams();
      if (warehouseId) qs.set("warehouse_id", warehouseId);
      if (search.trim()) qs.set("search", search.trim());
      setLoading(true);
      api
        .get(`/api/Inventory/Summary?${qs}`)
        .then((r) => setRows(unwrapObject<{ rows: StockSummaryRow[] }>(r).rows || []))
        .catch((err: any) => error("Fetch Error", err.message || "Failed to load stock"))
        .finally(() => setLoading(false));
    }, search.trim() ? 350 : 0);
    return () => clearTimeout(timer);
  }, [warehouseId, search, reloadKey]);

  if (loading && !rows.length) return <TableSkeleton rows={6} columns={6} />;
  return (
    <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Product</TableHead>
            <TableHead>Shade</TableHead>
            <TableHead className="text-right">Rolls</TableHead>
            <TableHead className="text-right">Lots</TableHead>
            <TableHead className="text-right">On hand</TableHead>
            <TableHead className="text-right">Reserved</TableHead>
            <TableHead className="text-right">Available</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.product_id}-${r.variant_id}-${r.unit}`} className="cursor-pointer hover:bg-muted/40" onClick={() => onOpenRolls(r)}>
              <TableCell>
                <div className="font-semibold text-sm">{r.product_name}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{r.product_code}</div>
              </TableCell>
              <TableCell className="text-sm">
                {r.variant_name ? (
                  <>
                    <Swatch hex={r.color_hex} />
                    {r.variant_name}
                  </>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-right text-sm">{r.unit_count}</TableCell>
              <TableCell className="text-right text-sm">{r.lot_count}</TableCell>
              <TableCell className="text-right text-sm font-semibold">{fmtQty(r.total_qty, r.unit)}</TableCell>
              <TableCell className="text-right text-sm text-amber-600">{r.reserved_qty ? fmtQty(r.reserved_qty, r.unit) : "-"}</TableCell>
              <TableCell className="text-right text-sm font-semibold text-emerald-600">{fmtQty(r.available_qty, r.unit)}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">
                No stock here yet. Receive goods or enter opening stock to get started.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
